import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, CardActionArea, CardContent, CardMedia, useMediaQuery, useTheme } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Text } from '~/components/ui/text';
import { DESKTOP_MIN_WIDTH } from '~/constants/common';
import useBuilderBlockMaxWidth from '~/hooks/useBuilderBlockMaxWidth';
import { COLORS } from '~/styles/colors';

import 'swiper/css/pagination';

import 'swiper/css';

type TemplateSliderProps = {
  images: (t: (key: string) => string) => Array<{
    title: string;
    component: string; // assuming `component` is an image src
  }>;
  selectSlide: (index: number) => void;
  defaultSelectedItem?: number;
};

export default function TemplateSlider({ images, selectSlide, defaultSelectedItem = 0 }: TemplateSliderProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const [selectedItem, setSelectedItem] = useState(defaultSelectedItem);
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));
  const desktopContainerWidth = useBuilderBlockMaxWidth();

  const handleItemClick = (index: number) => {
    selectSlide(index);
    setSelectedItem(index);
  };

  return (
    <Box sx={{ maxWidth: isDesktop ? desktopContainerWidth : 'calc(100dvw - 64px)' }}>
      <Swiper spaceBetween={8} slidesPerView="auto">
        {images(t)?.map((item, index) => (
          <SwiperSlide key={index} style={{ maxWidth: 156, borderRadius: '20px' }}>
            <TemplateCard
              title={item.title}
              component={item.component}
              index={index}
              selectedItem={selectedItem}
              setSelectItem={handleItemClick}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
}

type TemplateCardProps = {
  title: string;
  component: string;
  index: number;
  selectedItem: number;
  setSelectItem: (index: number) => void;
};
function TemplateCard({ title, selectedItem, setSelectItem, component, index }: TemplateCardProps) {
  const isSelected = selectedItem === index;

  return (
    <Card
      sx={{
        maxWidth: 156,
        maxHeight: 240,
        border: `${isSelected ? 2 : 1}px solid ${isSelected ? COLORS.primary : COLORS.grey200}`,
        boxShadow: 0,
        borderRadius: '20px',
        padding: '8px',
      }}
    >
      <CardActionArea onClick={() => setSelectItem(index)}>
        <CardContent sx={{ padding: 0 }}>
          <CardMedia component="img" height="197" src={component} sx={{ objectFit: 'fill', borderRadius: '20px' }} />
        </CardContent>
        <Text variant="inputSmall" sx={{ textAlign: 'center', marginTop: '8px', lineHeight: '19px' }}>
          {title}
        </Text>
      </CardActionArea>
    </Card>
  );
}
